import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, BRAND_INFO, CURRENCY, COUNTRY_OF_ORIGIN, SHIPPING_INFO, RETURN_ADDRESS } from '../data/mock';
import { FileText, Mail, Phone, MapPin, Building2 } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const TermsPage = () => {
  usePageTitle(
    'Terms & Conditions',
    'Read the Terms and Conditions governing purchases and use of the Stitch Your Dash website, operated by LP Capital Holdings LLC.'
  );

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[80px] md:pt-[120px] lg:pt-[136px] pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-rose-600" />
            </div>
            <h1 className="font-display text-3xl lg:text-4xl text-warmBrown-900 mb-4">Terms &amp; Conditions</h1>
            <p className="text-warmBrown-500">Last updated: July 2025</p>
          </div>

          <div className="bg-white rounded-3xl p-6 lg:p-10 shadow-card border border-cream-200 space-y-8">

            {/* Introduction */}
            <section>
              <p className="text-warmBrown-600 leading-relaxed">
                These Terms and Conditions (the &quot;Terms&quot;) govern your access to and use of the website
                located at <strong>{BRAND_INFO.domain}</strong> (the &quot;Site&quot;) and the purchase of any
                products offered through the Site (collectively, the &quot;Services&quot;). The Site and the Services
                are operated by <strong>{CONTACT_INFO.corporateName}</strong>, doing business as
                &quot;{BRAND_INFO.name}&quot; (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;).
                By accessing or using our Site, placing an order, or otherwise using the Services, you
                (&quot;you&quot; or &quot;Customer&quot;) agree to be bound by these Terms. If you do not agree,
                please do not use the Site or place an order.
              </p>
            </section>

            {/* Company Information */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">1. Company Information</h2>
              <p className="text-warmBrown-600 leading-relaxed mb-4">
                The Site is owned and operated by:
              </p>
              <div className="bg-cream-50 rounded-2xl p-5 border border-cream-200 space-y-2">
                <div className="flex items-start gap-2 text-warmBrown-700">
                  <Building2 className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">{CONTACT_INFO.corporateName}</p>
                    <p className="text-sm">d/b/a {BRAND_INFO.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-warmBrown-700">
                  <MapPin className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p>{CONTACT_INFO.address.street}</p>
                    <p>{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
                    <p>{CONTACT_INFO.address.country}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-warmBrown-700 text-sm">
                  <Phone className="w-5 h-5 text-rose-500" />
                  <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="hover:text-rose-600">
                    {CONTACT_INFO.phone}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-warmBrown-700 text-sm">
                  <Mail className="w-5 h-5 text-rose-500" />
                  <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-rose-600">
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </section>

            {/* Eligibility */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">2. Eligibility</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                You must be at least 18 years of age and legally capable of entering into binding
                contracts to use the Services or place an order. By placing an order, you represent
                and warrant that you meet these requirements and that all information you provide is
                accurate, current, and complete.
              </p>
            </section>

            {/* Description of Goods and Services */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">3. Description of Products</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                {BRAND_INFO.name} offers sewing tools, DIY craft supplies, and related accessories,
                including but not limited to cutting mats, fabric shears, presser foot kits, pattern
                drafting rulers, sewing clips, mini steam irons, stitch repair tools, and storage
                organizers. All products are physical goods that are shipped to the Customer&apos;s
                address. Product descriptions, images, and specifications are provided for
                informational purposes; colors and appearance may vary slightly from images due to
                display settings. We reserve the right to correct any errors, inaccuracies, or
                omissions, and to change or update product information at any time without prior
                notice.
              </p>
            </section>

            {/* Pricing and Currency */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">4. Pricing and Currency</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                All prices displayed on the Site are listed in <strong>United States Dollars ({CURRENCY})</strong>
                {' '}and are exclusive of applicable sales tax, shipping, and handling charges, which will be
                added to your order total at checkout. Prices are subject to change without notice. The
                price charged for an item will be the price in effect at the time the order is placed.
                In the event of a pricing error, we reserve the right to cancel the affected order and
                issue a full refund.
              </p>
            </section>

            {/* Orders and Acceptance */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">5. Orders and Acceptance</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                Your submission of an order constitutes an offer to purchase. All orders are subject
                to our acceptance and product availability. We reserve the right, in our sole
                discretion, to refuse, limit, cancel, or modify any order for any reason, including
                suspected fraud, unauthorized transactions, inventory limitations, or errors in pricing
                or product information. An order is not accepted until you receive an order
                confirmation email from us and the product has been shipped.
              </p>
            </section>

            {/* Payment and Billing Descriptor */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">6. Payment Methods &amp; Billing</h2>
              <p className="text-warmBrown-600 leading-relaxed mb-3">
                We accept the following payment methods: <strong>Visa, Mastercard, and American
                Express</strong>. <strong>Discover is not an accepted form of payment.</strong> Payment is
                processed securely using 256-bit SSL encryption. By submitting payment information, you
                represent and warrant that you are authorized to use the designated payment method and
                authorize us (or our third-party payment processor) to charge the full amount of your order,
                including applicable taxes and shipping, to the selected payment method.
              </p>
              <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-5 my-4 shadow-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-rose-700 font-bold mb-1">
                      Billing Descriptor
                    </p>
                    <p className="text-warmBrown-800 text-base leading-relaxed">
                      Charges from {BRAND_INFO.name} will appear on your credit or debit card statement as:
                    </p>
                    <p className="mt-2 inline-block bg-white border border-rose-200 rounded-lg px-3 py-1.5 font-mono text-sm font-bold text-rose-700 tracking-wider">
                      {BRAND_INFO.billingDescriptor}
                    </p>
                    <p className="mt-3 text-warmBrown-600 text-sm leading-relaxed">
                      Please save this information to help identify the charge on your statement and to
                      avoid disputes or chargebacks.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-warmBrown-600 leading-relaxed mt-3">
                If payment is declined for any reason, we may cancel your order. You are solely
                responsible for all charges incurred, including any taxes or duties. You agree that
                you will not use any payment method that you are not legally authorized to use.
              </p>
            </section>

            {/* Shipping and Delivery */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">7. Shipping and Delivery</h2>
              <p className="text-warmBrown-600 leading-relaxed mb-3">
                We currently ship only within the <strong>{COUNTRY_OF_ORIGIN}</strong>. Orders are
                shipped via USPS, UPS, or FedEx. Estimated delivery timeframes are:
              </p>
              <ul className="list-disc list-inside text-warmBrown-600 leading-relaxed space-y-1 ml-2">
                <li><strong>Standard Shipping (Domestic):</strong> {SHIPPING_INFO.domestic.standard.deliveryTime}</li>
                <li><strong>Express Shipping (Domestic):</strong> {SHIPPING_INFO.domestic.express.deliveryTime}</li>
                <li><strong>Free Shipping:</strong> On orders over $99 within the United States</li>
              </ul>
              <p className="text-warmBrown-600 leading-relaxed mt-3">
                Delivery times are estimates only and begin from the date of shipment, not the date
                of order. Delays may occur due to carrier issues, customs, weather, or other factors
                outside our control. Risk of loss and title for products pass to you upon delivery
                to the carrier. If a shipment is lost or damaged in transit, please contact us at
                {' '}<a href={`mailto:${CONTACT_INFO.email}`} className="text-rose-600 underline">{CONTACT_INFO.email}</a>
                {' '}within 14 days of the expected delivery date.
              </p>
            </section>

            {/* Returns, Refunds, and Cancellations */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">8. Returns, Refunds, Exchanges &amp; Cancellations</h2>
              <p className="text-warmBrown-600 leading-relaxed mb-3">
                We offer a <strong>30-day return policy</strong> from the date of delivery. To be
                eligible, items must be unused, in original packaging, and accompanied by proof of
                purchase. Shipping costs are non-refundable, and return shipping is the
                responsibility of the Customer unless the item was received damaged or incorrect.
              </p>
              <p className="text-warmBrown-600 leading-relaxed mb-3">
                Once your return is received and inspected, we will notify you of the approval or
                rejection of your refund. Approved refunds will be credited to your original method
                of payment within <strong>5&ndash;10 business days</strong>. Exchanges are processed
                subject to availability.
              </p>
              <p className="text-warmBrown-600 leading-relaxed mb-3">
                <strong>Order Cancellations:</strong> Orders may be cancelled within 24 hours of
                placement by contacting customer service at
                {' '}<a href={`mailto:${CONTACT_INFO.email}`} className="text-rose-600 underline">{CONTACT_INFO.email}</a>.
                Once an order has shipped, it cannot be cancelled but may be returned under this
                policy.
              </p>
              <div className="bg-cream-50 border border-cream-200 rounded-2xl p-4">
                <p className="text-warmBrown-700 text-sm leading-relaxed">
                  <strong>Return Shipping Address:</strong><br />
                  {CONTACT_INFO.corporateName} &ndash; Returns<br />
                  {RETURN_ADDRESS.street}<br />
                  {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}<br />
                  {RETURN_ADDRESS.country}
                </p>
              </div>
            </section>

            {/* Chargebacks */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">9. Chargebacks and Dispute Resolution</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                If you have any issue with your order, please contact our customer service team
                before initiating a chargeback with your card issuer. Most disputes can be resolved
                directly and quickly. You agree to work in good faith to resolve any dispute by
                first contacting us at
                {' '}<a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-rose-600 underline">{CONTACT_INFO.phone}</a>
                {' '}or
                {' '}<a href={`mailto:${CONTACT_INFO.email}`} className="text-rose-600 underline">{CONTACT_INFO.email}</a>.
                Fraudulent or bad-faith chargebacks may result in account suspension and may be
                reported to the appropriate authorities.
              </p>
            </section>

            {/* Warranty Disclaimer */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">10. Warranty Disclaimer</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                EXCEPT AS EXPRESSLY PROVIDED IN THESE TERMS, THE SITE AND ALL PRODUCTS ARE PROVIDED
                ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS. TO THE MAXIMUM EXTENT
                PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT
                LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                TITLE, AND NON-INFRINGEMENT. Our products are intended for hobby, craft, and home
                sewing use. Always follow instructions and safety precautions included with each
                product.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">11. Limitation of Liability</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL {CONTACT_INFO.corporateName.toUpperCase()},
                ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO
                LOST PROFITS, DATA LOSS, OR PERSONAL INJURY, ARISING OUT OF OR RELATED TO YOUR USE OF
                THE SITE OR THE PRODUCTS. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE
                AMOUNT YOU PAID FOR THE SPECIFIC PRODUCT GIVING RISE TO THE CLAIM.
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">12. Indemnification</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                You agree to defend, indemnify, and hold harmless {CONTACT_INFO.corporateName} and
                its affiliates, officers, employees, and agents from and against any claims,
                liabilities, damages, losses, and expenses (including reasonable attorneys&apos;
                fees) arising out of or in any way connected with your violation of these Terms,
                your misuse of the Services, or your violation of any law or the rights of a third
                party.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">13. Intellectual Property</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                All content on the Site &mdash; including text, graphics, logos, images, photographs,
                and software &mdash; is the property of {CONTACT_INFO.corporateName} or its
                licensors and is protected by United States and international copyright, trademark,
                and other intellectual property laws. You may not copy, reproduce, distribute, or
                create derivative works from any content without our prior written consent.
              </p>
            </section>

            {/* User Conduct */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">14. Acceptable Use</h2>
              <p className="text-warmBrown-600 leading-relaxed mb-2">
                You agree not to:
              </p>
              <ul className="list-disc list-inside text-warmBrown-600 leading-relaxed space-y-1 ml-2">
                <li>Use the Site for any unlawful purpose or in violation of any applicable law;</li>
                <li>Submit false, inaccurate, or misleading information;</li>
                <li>Engage in fraudulent transactions or unauthorized use of payment methods;</li>
                <li>Attempt to gain unauthorized access to the Site, its servers, or related systems;</li>
                <li>Interfere with or disrupt the Site or the Services;</li>
                <li>Resell or commercially exploit any product or content from the Site without authorization.</li>
              </ul>
            </section>

            {/* Privacy */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">15. Privacy</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                Your privacy is important to us. Our collection and use of personal information in
                connection with the Services is governed by our{' '}
                <a href="/privacy" className="text-rose-600 underline">Privacy Policy</a>, which is
                incorporated into these Terms by reference.
              </p>
            </section>

            {/* Electronic Communications */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">16. Electronic Communications</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                By placing an order or creating an account, you consent to receive electronic
                communications from us, including order confirmations, shipping notifications, and
                other transactional messages. You agree that all agreements, notices, and
                disclosures provided electronically satisfy any legal requirement that such
                communications be in writing.
              </p>
            </section>

            {/* Export and Territorial Restrictions */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">17. Territorial Restrictions &amp; Export</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                Our products are intended for sale and delivery only within the United States. We
                make no representation that content on the Site is appropriate or available for use
                in other locations. You may not use or export products in violation of U.S. export
                laws and regulations.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">18. Governing Law &amp; Jurisdiction</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the
                State of California, without regard to its conflict of law principles. Any dispute
                arising out of or relating to these Terms or the Services shall be resolved
                exclusively in the state or federal courts located in Los Angeles County,
                California, and you consent to the personal jurisdiction of such courts.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">19. Modifications to Terms</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                We reserve the right to modify these Terms at any time. Updated Terms will be
                posted on the Site with a new &quot;Last updated&quot; date. Your continued use of
                the Site or the Services following any change constitutes your acceptance of the
                revised Terms. It is your responsibility to review these Terms periodically.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">20. Termination</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                We may suspend or terminate your access to the Site or the Services at any time,
                without notice, for conduct that we believe violates these Terms or is harmful to
                other users, us, or third parties, or for any other reason in our sole discretion.
              </p>
            </section>

            {/* Severability & Entire Agreement */}
            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">21. Severability &amp; Entire Agreement</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                If any provision of these Terms is held to be invalid or unenforceable, the
                remaining provisions shall remain in full force and effect. These Terms, together
                with our Privacy Policy, Return &amp; Refund Policy, and Shipping Policy, constitute
                the entire agreement between you and {CONTACT_INFO.corporateName} regarding your
                use of the Site and the Services, and supersede any prior agreements.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-rose-50 rounded-2xl p-6 border border-rose-100">
              <h2 className="font-display text-lg text-warmBrown-900 mb-4">22. Contact Us</h2>
              <p className="text-warmBrown-600 mb-4">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2 text-warmBrown-700">
                  <Building2 className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">{CONTACT_INFO.corporateName}</p>
                    <p>d/b/a {BRAND_INFO.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 text-warmBrown-700">
                  <MapPin className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <p>
                    {CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                  </p>
                </div>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-2 text-rose-600 hover:text-rose-700 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  {CONTACT_INFO.email}
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-2 text-rose-600 hover:text-rose-700 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {CONTACT_INFO.phone}
                </a>
              </div>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
